/**
 * DefencIQ Storage Helper
 * Handles local storage operations for assessment state management
 * Supports auto-save, draft restoration, reset, and export
 */

const STORAGE_KEYS = {
  CURRENT_ASSESSMENT: 'defenciq_current_assessment',
  ASSESSMENT_LIST: 'defenciq_assessment_list',
  SETTINGS: 'defenciq_settings'
};

const AUTO_SAVE_DEBOUNCE_MS = 1000;

let autoSaveTimer = null;

/**
 * Initialize default state structure aligned to schema
 * @returns {Object} Default assessment state
 */
export const createDefaultState = () => ({
  engagement: {
    assessment_id: `DIQ-${Date.now()}`,
    client_name: '',
    assessor_name: '',
    assessment_date: new Date().toISOString().split('T')[0],
    engagement_type: '',
    notes: ''
  },
  answers: {},
  currentSection: 'engagement',
  lastModified: null,
  version: '1.0.0'
});

/**
 * Save current assessment state to local storage
 * @param {Object} state - Current assessment state
 * @returns {boolean} Success status
 */
export const saveAssessment = (state) => {
  try {
    const saveData = {
      ...state,
      lastModified: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEYS.CURRENT_ASSESSMENT, JSON.stringify(saveData));
    updateAssessmentList(saveData);
    return true;
  } catch (error) {
    console.error('Failed to save assessment:', error);
    return false;
  }
};

/**
 * Auto-save with debounce
 * @param {Object} state - Current assessment state
 */
export const autoSaveAssessment = (state) => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
  autoSaveTimer = setTimeout(() => {
    saveAssessment(state);
  }, AUTO_SAVE_DEBOUNCE_MS);
};

/**
 * Load current assessment from local storage
 * @returns {Object|null} Assessment state or null
 */
export const loadAssessment = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.CURRENT_ASSESSMENT);
    if (!data) return null;

    const state = JSON.parse(data);
    
    // Validate structure
    if (!state.engagement || !state.answers) {
      return null;
    }

    return state;
  } catch (error) {
    console.error('Failed to load assessment:', error);
    return null;
  }
};

/**
 * Check if a saved assessment exists
 * @returns {boolean} Whether assessment exists
 */
export const hasAssessment = () => {
  return localStorage.getItem(STORAGE_KEYS.CURRENT_ASSESSMENT) !== null;
};

/**
 * Reset current assessment (clear state)
 * @returns {boolean} Success status
 */
export const resetAssessment = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_ASSESSMENT);
    return true;
  } catch (error) {
    console.error('Failed to reset assessment:', error);
    return false;
  }
};

/**
 * Update the list of saved assessments
 * @param {Object} state - Current assessment state
 */
const updateAssessmentList = (state) => {
  try {
    let list = getAssessmentList();

    // Remove existing entry for same assessment ID
    list = list.filter(a => a.assessment_id !== state.engagement?.assessment_id);

    // Add new entry
    list.unshift({
      assessment_id: state.engagement?.assessment_id,
      client_name: state.engagement?.client_name || 'Unknown Client',
      assessment_date: state.engagement?.assessment_date,
      lastModified: state.lastModified,
      currentSection: state.currentSection
    });

    // Keep only last 20 assessments
    list = list.slice(0, 20);

    localStorage.setItem(STORAGE_KEYS.ASSESSMENT_LIST, JSON.stringify(list));
  } catch (error) {
    console.error('Failed to update assessment list:', error);
  }
};

/**
 * Get list of saved assessments
 * @returns {Array} Assessment list
 */
export const getAssessmentList = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.ASSESSMENT_LIST);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to get assessment list:', error);
    return [];
  }
};

/**
 * Download data as JSON file
 * @param {Object} data - Data to download
 * @param {string} filename - Filename for download
 */
export const downloadAsJSON = (data, filename) => {
  const json = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

/**
 * Save user settings
 * @param {Object} settings - Settings to save
 */
export const saveSettings = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save settings:', error);
  }
};

/**
 * Load user settings
 * @returns {Object} Saved settings
 */
export const loadSettings = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Failed to load settings:', error);
    return {};
  }
};

/**
 * Get answer for a specific field
 * @param {Object} answers - All answers
 * @param {string} fieldId - Field ID
 * @returns {Object|undefined} Answer or undefined
 */
export const getAnswer = (answers, fieldId) => {
  return answers?.[fieldId];
};

/**
 * Set answer for a specific field
 * @param {Object} answers - All answers
 * @param {string} fieldId - Field ID
 * @param {Object} answer - Answer object { status, score, answer, notes }
 * @returns {Object} Updated answers
 */
export const setAnswer = (answers, fieldId, answer) => {
  return {
    ...answers,
    [fieldId]: {
      ...answers[fieldId],
      ...answer
    }
  };
};

/**
 * Count answered questions
 * @param {Object} answers - All answers
 * @returns {number} Count of answered questions
 */
export const countAnswered = (answers) => {
  return Object.keys(answers).filter(key => {
    const answer = answers[key];
    return answer && (answer.status || answer.answer !== undefined);
  }).length;
};

/**
 * Check if section is complete
 * @param {string} sectionId - Section ID
 * @param {Object} answers - All answers
 * @param {Array} questions - Section questions
 * @returns {boolean} Is complete
 */
export const isSectionComplete = (sectionId, answers, questions) => {
  const requiredQuestions = questions.filter(q => q.required !== false);
  return requiredQuestions.every(q => {
    const answer = answers[q.field_id];
    return answer && (answer.status || answer.answer !== undefined);
  });
};

export default {
  createDefaultState,
  saveAssessment,
  autoSaveAssessment,
  loadAssessment,
  hasAssessment,
  resetAssessment,
  getAssessmentList,
  downloadAsJSON,
  copyToClipboard,
  saveSettings,
  loadSettings,
  getAnswer,
  setAnswer,
  countAnswered,
  isSectionComplete
};
